<script setup lang="ts">
import { Plus } from 'lucide-vue-next'
import axios from 'axios'

import EmptyState from '@/components/EmptyState.vue'
import TemplateModal from '@/components/TemplateModal.vue'
import SurveysItems from './Components/SurveysItems.vue'
import type { Survey } from '@/types/types'

const { t } = useI18n()
const { stores } = useLocalStorage()
const { selectedStoreFilter } = useStore()

const isLoaded = ref(false)
const surveys = ref<Survey[]>([])
const templates = ref([])
const { fetchTemplates } = useTemplate()
const storeFilter = ref<number[]>(selectedStoreFilter.value.length ? [...selectedStoreFilter.value] : [])

const currentPage = ref(1)
const pageSize = 10
const isFetching = ref(false)
const totalPage = ref(1)
const lastPage = ref(1)

const pageTitle = {
  title: 'pages.survey.surveyPage.title'
}

const isTemplateTipsOpen = ref(false)
const hasFetchedTemplates = ref(false)

const openTemplatesTips = async () => {
  if (!hasFetchedTemplates.value) {
    await fetchOnceTemplates()
  }
  isTemplateTipsOpen.value = true
}

const fetchOnceTemplates = async () => {
  templates.value = await fetchTemplates('survey-question')
  hasFetchedTemplates.value = true
}

const fetchData = async (page = 1) => {
  currentPage.value = page
  isFetching.value = true

  try {
    const urlParams: {
      page: number
      per_page: number
      store_ids?: number[]
    } = {
      page: currentPage.value,
      per_page: pageSize
    }

    const isAllStoresSelected = storeFilter.value.length === stores.value.length
    if (storeFilter.value.length > 0 && !isAllStoresSelected) {
      urlParams.store_ids = storeFilter.value
    }

    const { data } = await axios.get<{
      data: Survey[]
      meta: { last_page: number; total: number }
    }>('/surveys', {
      params: urlParams
    })

    surveys.value = data.data
    totalPage.value = data.meta.total
    lastPage.value = data.meta.last_page

    return data.meta.total > page * pageSize
  } catch {
    return false
  } finally {
    isFetching.value = false
    isLoaded.value = true
  }
}

watch(storeFilter, async (newValue) => {
  selectedStoreFilter.value = [...newValue]
  await fetchData()
})

fetchData()
</script>

<template>
  <div>
    <div v-if="isLoaded">
      <div>
        <TheListHeader :title="t(pageTitle.title)">
          <template #actions>
            <Button v-if="surveys.length > 0" variant="default-primary" @click="openTemplatesTips">
              <Plus />
              {{ t('pages.survey.surveyPage.cta.create') }}
            </Button>
          </template>
        </TheListHeader>
        <TemplateModal
          :is-open="isTemplateTipsOpen"
          module="survey-question"
          :templates="templates"
          @on-close-modal="isTemplateTipsOpen = false"
        />
        <div class="px-6 pb-6 sm:px-12 sm:pb-12">
          <div class="mb-6 flex items-center">
            <MultiSelectStores v-model="storeFilter" />
          </div>
          <div v-if="surveys.length > 0">
            <SurveysItems
              :surveys="surveys"
              :total="totalPage"
              :current-page="currentPage"
              :last-page="lastPage"
              :is-fetching="isFetching"
              @change-page="fetchData"
            />
          </div>
          <EmptyState v-else module="survey-question" />
        </div>
      </div>
    </div>
    <TheLoadingPage v-else />
  </div>
</template>
